import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Switch, Typography, Tag, Space, Tooltip, Divider, Alert, Row, Col, Grid } from 'antd';
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";
import DataGrid from "../../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import Helper from "../../../core/Helper";
import _ from "lodash";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { useParams } from "react-router-dom";
import { PatientSummaryRow } from "./PatientSummaryRow";
import { PatientSummaryCard } from "./PatientSummaryCard";
import AppointmentStateColumn from "../appointment-datagrid/AppointmentStateColumn";
import AppointmentIdColumn from "../appointment-datagrid/AppointmentIdColumn";
import { BankOutlined, CalendarOutlined, PlusOutlined, WalletOutlined, FilterOutlined, CheckCircleOutlined, MedicineBoxOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const AppointmentPatientDataGrid = ({ filter, tableUpdate, onRows, rows, openCalendar, onPayment }) => {
    const params = useParams();
    const prevRows = useRef([])
    const isPageLoad = useRef(false)
    const [selectedTotal, setSelectedTotal] = useState(0)
    const { hasPermission, formatPrice } = useUser();
    const { setData, setPaginationResult, queryModel, setDataGridProps, selectedRows, onFilterPageSort, setSelectedRows, resultModel, refreshDataGrid } = useDataGrid();
    const screens = Grid.useBreakpoint();
    const isMobileLayout = !screens.md;

    // --- DATA LOADING ---
    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: Helper.Date.beginOfDay(queryModel.filter.startDate),
                endDate: Helper.Date.endOfDay(queryModel.filter.endDate),
            }
        }

        input.paginationQuery = {
            pageNumber: 1,
            pageSize: 10000, // Client-side pagination gibi davranıyor
        }

        if (!input.filter.patientId) return

        const res = await ApiService.Appointment.GetAppointmentDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    // --- SEÇİLENLERİN TOPLAMINI HESAPLA ---
    const getTotalBalance = async (selectedIds) => {
        if (selectedIds.length === 0) {
            setSelectedTotal(0);
            return;
        }
        const res = await ApiService.Appointment.GetTotalPatientBalance(params.patientId, selectedIds);
        if (res.success) {
            setSelectedTotal(res.data.selectedTotal)
        }
    }

    // --- KOLON TANIMLARI ---
    const getColumns = () => {
        return [
            {
                width: 60,
                key: 'appointmentId',
                dataIndex: 'appointmentId',
                title: t('Id'),
                render: (text, row) => <AppointmentIdColumn text={text} row={row} onSelect={(row) => setSelectedRows([row])} />
            },
            {
                key: 'startDate',
                dataIndex: 'startDate',
                sorter: true,
                title: t('Date'),
                width: 130,
                render: (text, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 500 }}>{Helper.Date.dateFormat(text).split(' ')[0]}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#888', fontSize: 11 }}>
                            <CalendarOutlined />
                            {Helper.Date.dateFormat(text).split(' ')[1]}
                        </div>
                    </div>
                )
            },
            {
                key: 'details',
                title: t('Details'),
                width: 220,
                render: (_, row) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Space>
                                <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                                <Text style={{ fontSize: 11 }} ellipsis={{ tooltip: row.unitName }}>{row.unitName}</Text>
                            </Space>
                            {row.institutionName && (
                                <Space size={2} style={{ fontSize: 11, color: '#888' }}>
                                    <BankOutlined />
                                    <Tooltip title={row.institutionName}>
                                        <span style={{ maxWidth: 100, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                                            {row.institutionName}
                                        </span>
                                    </Tooltip>
                                </Space>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tooltip title={row.appointmentTypeName}>
                                <Text style={{ maxWidth: 130 }} ellipsis>{row.appointmentTypeName}</Text>
                            </Tooltip>
                        </div>
                    </div>
                )
            },
            {
                key: 'stateName',
                dataIndex: 'stateName',
                columnName: 'state.name',
                sorter: true,
                title: t('State'),
                width: 100,
                render: (text, row) => <AppointmentStateColumn text={text} row={row} />
            },
            // FİNANSAL KOLONLAR (Ayrıştırılmış)
            {
                key: 'totalPrice',
                dataIndex: 'totalPrice',
                sorter: true,
                title: t('Price'),
                align: 'right',
                width: 100,
                render: (text, row) => <Text type="secondary">{text === -1 ? '-' : formatPrice(text, { currency: row?.currency || 'TRY' })}</Text>
            },
            {
                key: 'totalPaid',
                dataIndex: 'totalPaid',
                sorter: true,
                title: t('Paid'),
                align: 'right',
                width: 100,
                render: (text, row) => <Text style={{ color: text > 0 ? '#389e0d' : 'inherit' }}>{text === -1 ? '-' : formatPrice(text, { currency: row?.currency || 'TRY' })}</Text>
            },
            {
                key: 'balance',
                dataIndex: 'balance',
                title: t('Balance'),
                sorter: true,
                align: 'right',
                width: 110,
                render: (text, row) => {
                    const hasDebt = row.balance > 0;
                    if (row.totalPrice === -1) return '-';
                    return <Text strong style={{ color: hasDebt ? '#cf1322' : '#389e0d' }}>{formatPrice(row.balance, { currency: row?.currency || 'TRY' })}</Text>
                }
            },
        ]
    }

    // --- EFFECTS ---
    useEffect(() => { load() }, [queryModel.updateKey])
    useEffect(() => { if (tableUpdate) refreshDataGrid() }, [tableUpdate]);
    useEffect(() => { onFilterPageSort({ filter: { ...filter } }) }, [filter]);

    // --- MOBİL KART RENDER ---
    const renderCard = (row, { isSelected }) => {
        const hasDebt = row.balance > 0;
        const dateStr = Helper.Date.dateFormat(row.startDate);
        const [datePart, timePart] = (dateStr || '').split(' ');

        return (
            <div>
                {/* ÜST: ID + Tarih + Durum */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                            <Text strong style={{ fontSize: 15 }}>{datePart}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <CalendarOutlined style={{ marginRight: 3 }} />
                                {timePart}
                            </Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>ID: {row.appointmentId}</Text>
                    </div>
                    {row.stateName && (
                        <Tag color={row.stateColor || 'default'} style={{ margin: 0, fontSize: 12 }}>
                            {row.stateName}
                        </Tag>
                    )}
                </div>

                {/* ORTA: Birim + Kurum + Tip */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, fontSize: 13 }}>
                    {row.unitName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1890ff' }}>
                            <MedicineBoxOutlined />
                            <span>{row.unitName}</span>
                        </div>
                    )}
                    {row.institutionName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#595959' }}>
                            <BankOutlined />
                            <span>{row.institutionName}</span>
                        </div>
                    )}
                    {row.appointmentTypeName && (
                        <div style={{ color: '#8c8c8c', fontSize: 12, paddingLeft: 22 }}>
                            {row.appointmentTypeName}
                        </div>
                    )}
                </div>

                {/* ALT: Finansal */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px dashed #f0f0f0',
                    paddingTop: 8,
                    gap: 8
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: '#8c8c8c', minWidth: 0 }}>
                        <span>
                            {t('Price')}: <Text style={{ fontSize: 12, color: '#595959' }}>
                                {row.totalPrice === -1 ? '-' : formatPrice(row.totalPrice, { currency: row?.currency || 'TRY' })}
                            </Text>
                        </span>
                        <span>
                            {t('Paid')}: <Text style={{ fontSize: 12, color: row.totalPaid > 0 ? '#389e0d' : '#595959' }}>
                                {row.totalPaid === -1 ? '-' : formatPrice(row.totalPaid, { currency: row?.currency || 'TRY' })}
                            </Text>
                        </span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 10, color: '#8c8c8c' }}>{t('Balance')}</div>
                        <div style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: row.totalPrice === -1 ? '#8c8c8c' : (hasDebt ? '#cf1322' : '#389e0d')
                        }}>
                            {row.totalPrice === -1 ? '-' : formatPrice(row.balance, { currency: row?.currency || 'TRY' })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        let columns = getColumns()
        let props = {
            rowKey: 'appointmentId',
            enableRowSelection: onRows, // Seçim aktif
            expandedRowRender: false,
            columns: columns,
            rowClassName: (row) => row.isDeleted ? 'deleted-row' : '',
            cardRender: renderCard,
            cardSummary: () => <PatientSummaryCard />,
            antdTableProps: {
                summary: () => <PatientSummaryRow />,
                scroll: { x: 800 },
                size: 'small' // Daha kompakt liste
            }
        }
        props.antdTableProps.pagination = false
        setDataGridProps(props)
    }, [])

    useEffect(() => {
        onRows && onRows(selectedRows)
        if (selectedRows.length > 0) {
            const selectedIds = selectedRows.map(row => row.appointmentId);
            getTotalBalance(selectedIds);
        } else {
            setSelectedTotal(0);
        }
    }, [selectedRows]);

    useEffect(() => {
        if (!_.isEqual(prevRows.current, rows)) {
            prevRows.current = rows
            setSelectedRows(rows)
        }
    }, [rows]);

    useEffect(() => {
        if (!isPageLoad.current && params.appointmentId) {
            isPageLoad.current = true
            setSelectedRows([{ appointmentId: parseInt(params.appointmentId) }])
        }
    }, [resultModel.data])

    // --- RENDER TOOLBAR ---
    const renderToolbar = () => {
        const showAllSwitch = (
            <Space size={6}>
                <Switch
                    size="small"
                    checked={!(queryModel.filter.stateGroupIdList?.length === 1)}
                    onChange={(checked) => {
                        onFilterPageSort({
                            filter: { ...queryModel.filter, stateGroupIdList: checked ? [] : [1] }
                        })
                    }}
                />
                <Text style={{ fontSize: 13 }}>{t('Show All')}</Text>
            </Space>
        );

        const debtOnlySwitch = (
            <Space size={6}>
                <Switch
                    size="small"
                    checked={queryModel.filter.hideZeroBalance}
                    onChange={(checked) => {
                        onFilterPageSort({
                            filter: { ...queryModel.filter, hideZeroBalance: checked }
                        })
                    }}
                />
                <Text style={{ fontSize: 13 }}>{t('Debt Only')}</Text>
            </Space>
        );

        const selectedBlock = selectedRows.length > 0 && (
            <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <Text>{t('Total Selected')}:</Text>
                    <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                        {formatPrice(selectedTotal)}
                    </Text>
                </div>

                {hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT) && (
                    <Button
                        type="primary"
                        danger
                        icon={<WalletOutlined />}
                        size={isMobileLayout ? 'middle' : 'small'}
                        onClick={onPayment}
                        block={isMobileLayout}
                    >
                        {t('Receive Payment')}
                    </Button>
                )}
            </>
        );

        const newAppointmentButton = hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT) && (
            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCalendar}
                size={isMobileLayout ? 'middle' : 'small'}
                block={isMobileLayout}
            >
                {t('New Appointment')}
            </Button>
        );

        // MOBİL: Dikey stack
        if (isMobileLayout) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                    {/* Switches yan yana, sığmazsa wrap */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        background: '#fafafa',
                        padding: '6px 10px',
                        borderRadius: 4
                    }}>
                        {showAllSwitch}
                        {debtOnlySwitch}
                    </div>

                    {/* Seçili toplam + Ödeme butonu (varsa) */}
                    {selectedRows.length > 0 && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 6,
                            background: '#e6f7ff',
                            padding: '8px 10px',
                            borderRadius: 4,
                            border: '1px solid #91d5ff'
                        }}>
                            {selectedBlock}
                        </div>
                    )}

                    {/* Yeni randevu — her zaman alt */}
                    {newAppointmentButton}
                </div>
            );
        }

        // DESKTOP: Mevcut yatay layout
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, flex: 1 }}>
                <Space size="small">
                    {showAllSwitch}
                    {debtOnlySwitch}
                </Space>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {selectedBlock}
                    {newAppointmentButton}
                </div>
            </div>
        );
    };

    return (
        <div>
            <Card
                bordered={false}
                style={{ backgroundColor: 'transparent', boxShadow: 'none' }} // İç içe kart görünümünü kaldırdım
                bodyStyle={{ padding: 0 }}
            >
                <DataGrid
                    name={'appointments'}
                    title={renderToolbar}
                />
            </Card>
        </div>
    )
}